/**
 * Personal contacts sync manager for user's own contacts
 */

import { BaseSyncManager } from './BaseSyncManager';
import { GraphApiClient } from './GraphApiClient';
import { SyncOptions, SyncType, DeltaResponse } from './types';

export class PersonalSyncManager extends BaseSyncManager {
  private isOutlookContext: boolean;

  constructor(isOutlookContext: boolean = true) {
    super(isOutlookContext);
    this.isOutlookContext = isOutlookContext;
  }

  protected getSyncType(): SyncType {
    return 'personal';
  }

  protected getMetaKey(): string {
    return 'personal';
  }

  protected async fetchDataDelta(accessToken: string, deltaToken: string, userUpn: string): Promise<any> {
    return GraphApiClient.fetchPersonalContactsDelta(accessToken, userUpn, deltaToken);
  }

  protected async fetchDataPage(accessToken: string, skipToken: string, top: number, userUpn: string): Promise<any> {
    return GraphApiClient.fetchPersonalContactsPage(accessToken, userUpn, skipToken, top);
  }

  protected async processDeltaQuery(accessToken: string, deltaToken: string, userUpn: string): Promise<DeltaResponse> {
    console.log('Processing personal delta query to get final delta token...');
    
    let currentDeltaToken = deltaToken;
    let nextLink: string | null = null;
    let finalDeltaToken: string | null = null;
    let pageCount = 0;
    
    do {
      pageCount++;
      console.log(`Processing personal delta query page ${pageCount}...`);
      
      const response = await this.fetchDataDelta(accessToken, currentDeltaToken, userUpn);
      
      // Update next link and delta token
      nextLink = response['@odata.nextLink'];
      finalDeltaToken = response['@odata.deltaLink'];
      
      console.log(`Personal page ${pageCount} response:`, {
        hasNextLink: !!nextLink,
        hasDeltaLink: !!finalDeltaToken,
        contactCount: response.value?.length || 0
      });
      
      // If we have a next link, use it for the next iteration
      if (nextLink) {
        currentDeltaToken = nextLink;
      }
      
      // Safety check to prevent infinite loops
      if (pageCount > 50) {
        console.warn('Personal delta query processing stopped after 50 pages to prevent infinite loop');
        break;
      }
      
      // Add a small delay between pages to prevent overwhelming the API
      if (nextLink) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
    } while (nextLink);
    
    console.log(`Personal delta query processing complete after ${pageCount} pages`);
    return { deltaToken: finalDeltaToken };
  }

  protected getSourceType(): string {
    return 'personal';
  }

  /**
   * Filter contacts to only include those with email addresses (Outlook only)
   */
  private filterValidContacts(contacts: any[]): any[] {
    // Only filter for Outlook context, not Teams
    if (!this.isOutlookContext) {
      return contacts;
    }
    
    return contacts.filter((contact: any) => {
      const email = contact.emailAddresses?.[0]?.address;
      return email && email.trim() !== '';
    });
  }

  /**
   * Bootstrap sync for personal contacts - initial full download of user's own contacts
   */
  async bootstrapPersonalSync(
    accessToken: string, 
    userUpn: string, 
    options: SyncOptions = {}
  ): Promise<void> {
    console.log('bootstrapPersonalSync called with:', { userUpn, hasAccessToken: !!accessToken });
    
    if (this.isSyncing && !options.skipSearchIndex) {
      throw new Error('Sync already in progress');
    }

    this.isSyncing = true;
    const { onProgress, onError, batchSize = 500 } = options;

    // Set a timeout to prevent sync from getting stuck
    this.setSyncTimeout(5 * 60 * 1000, () => {
      onError?.(new Error('Personal bootstrap sync timeout'));
    });

    try {
      onProgress?.({
        current: 0,
        total: 0,
        phase: 'bootstrap',
        message: 'Starting personal contacts bootstrap sync...'
      });

      // Check if personal bootstrap is already complete
      const meta = await this.store.getMeta();
      if (meta.personalBootstrapComplete) {
        onProgress?.({
          current: 1,
          total: 1,
          phase: 'bootstrap',
          message: 'Personal bootstrap already complete, skipping...'
        });
        return;
      }

      // Use fast pagination for initial sync, then get delta token
      console.log('Starting fast personal bootstrap sync...');
      console.log('Fetching personal contacts from Graph API...');
      const initialResponse = await this.fetchDataPage(accessToken, '', 999, userUpn);
      console.log('Personal contacts API response received:', {
        hasValue: !!initialResponse.value,
        contactCount: initialResponse.value?.length || 0,
        hasNextLink: !!initialResponse['@odata.nextLink'],
        hasDeltaLink: !!initialResponse['@odata.deltaLink']
      });
      
      let processedContacts = 0;
      let nextLink = initialResponse['@odata.nextLink'];
      let deltaLink = initialResponse['@odata.deltaLink'];
      
      console.log('Personal initial response:', {
        hasNextLink: !!nextLink,
        hasDeltaLink: !!deltaLink,
        contactCount: initialResponse.value?.length || 0
      });

      onProgress?.({
        current: 0,
        total: 0, // We don't know total yet
        phase: 'bootstrap',
        message: 'Starting personal contacts bootstrap sync...'
      });

      // Process initial batch - filter for contacts with email addresses
      const initialContacts = this.filterValidContacts(initialResponse.value || []);
      console.log(`Personal contacts after filtering: ${initialContacts.length} out of ${initialResponse.value?.length || 0}`);
      const normalizedContacts = await this.normalizer.normalizeContacts(initialContacts, userUpn, 'personal');
      
      await this.store.bulkUpsertContacts(normalizedContacts);
      processedContacts += initialContacts.length;

      onProgress?.({
        current: processedContacts,
        total: 0, // Still unknown
        phase: 'bootstrap',
        message: `Processed ${processedContacts} personal contacts so far...`
      });

      // Process remaining pages
      while (nextLink) {
        const response = await GraphApiClient.fetchContactsPageFromUrl(accessToken, nextLink);
        const contacts = this.filterValidContacts(response.value || []);
        
        if (contacts.length > 0) {
          const normalizedContacts = await this.normalizer.normalizeContacts(contacts, userUpn, 'personal');
          
          await this.store.bulkUpsertContacts(normalizedContacts);
          processedContacts += contacts.length;

          onProgress?.({
            current: processedContacts,
            total: 0, // Unknown total
            phase: 'bootstrap',
            message: `Processed ${processedContacts} personal contacts so far...`
          });
        }

        nextLink = response['@odata.nextLink'];
        deltaLink = response['@odata.deltaLink'];
      }

      // Get delta token for future incremental syncs (skip if requested)
      let finalDeltaToken = deltaLink;
      if (!finalDeltaToken && !options.skipDeltaToken) {
        console.log('Getting personal delta token for future incremental syncs...');
        try {
          // Process delta query to get the final delta token (no progress updates)
          const deltaResponse = await this.processDeltaQuery(accessToken, '', userUpn);
          finalDeltaToken = deltaResponse.deltaToken;
          console.log('Personal delta token obtained:', finalDeltaToken ? 'Yes' : 'No');
          if (finalDeltaToken) {
            console.log('Personal delta token preview:', finalDeltaToken.substring(0, 100) + '...');
          }
        } catch (error) {
          console.warn('Failed to get personal delta token:', error);
        }
      } else if (finalDeltaToken) {
        console.log('Using existing personal delta link from bootstrap sync');
      } else if (options.skipDeltaToken) {
        console.log('Skipping personal delta token generation (will be done in background)');
      }

      // Update meta with personal bootstrap completion
      await this.markBootstrapComplete(processedContacts, finalDeltaToken);
      
      // Log the delta token for debugging
      if (finalDeltaToken) {
        console.log('Personal bootstrap sync completed with delta token:', finalDeltaToken.substring(0, 100) + '...');
      } else {
        console.warn('Personal bootstrap sync completed but no delta token was obtained');
      }

      // Build search index after personal bootstrap sync
      await this.buildSearchIndex(options);

      // Only show progress if not skipping search index (i.e., not in parallel mode)
      if (!options.skipSearchIndex) {
        onProgress?.({
          current: processedContacts,
          total: processedContacts,
          phase: 'bootstrap',
          message: `Personal bootstrap sync complete! ${processedContacts} contacts synced`
        });
      }

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Personal bootstrap sync failed');
      console.error('Personal bootstrap sync failed:', err);
      onError?.(err);
      throw err;
    } finally {
      this.isSyncing = false;
      this.clearSyncTimeout();
    }
  }

  /**
   * Incremental sync for personal contacts using delta tokens
   */
  async incrementalPersonalSync(
    accessToken: string,
    userUpn: string,
    options: SyncOptions = {}
  ): Promise<void> {
    this.isSyncing = true;
    const { onProgress, onError } = options;

    try {
      const meta = await this.store.getMeta();
      
      
      if (!meta.personalBootstrapComplete) {
        throw new Error('Personal bootstrap sync must be completed before incremental sync');
      }

      if (!meta.personalDeltaToken) {
        console.warn('No personal delta token available; attempting regeneration...');
        try {
          await this.generateDeltaToken(accessToken, userUpn);
        } catch {}
        const refreshed = await this.store.getMeta();
        if (!refreshed.personalDeltaToken) {
          console.error('Personal delta token still missing after regeneration; skipping incremental run');
          return;
        }
      }

      onProgress?.({
        current: 0,
        total: 0,
        phase: 'incremental',
        message: 'Starting personal contacts incremental sync...'
      });

      // Fetch changes using delta token
      console.log('Starting personal incremental sync with delta token:', meta.personalDeltaToken?.substring(0, 50) + '...');
      
      let response;
      try {
        response = await this.fetchDataDelta(accessToken, (await this.store.getMeta()).personalDeltaToken as string, userUpn);
      } catch (error) {
        console.error('Personal delta sync failed; clearing token and attempting regeneration:', error);
        await this.clearInvalidDeltaToken();
        try { await this.generateDeltaToken(accessToken, userUpn); } catch {}
        const refreshed = await this.store.getMeta();
        if (!refreshed.personalDeltaToken) {
          console.error('Could not recover personal delta token; skipping incremental run');
          return;
        }
        response = await this.fetchDataDelta(accessToken, refreshed.personalDeltaToken, userUpn);
      }
      const contacts = response.value || [];
      const newDeltaLink = response['@odata.deltaLink'];
      
      console.log('Personal incremental delta response:', {
        hasNewDeltaLink: !!newDeltaLink,
        contactCount: contacts.length,
        hasChanges: contacts.length > 0
      });

      let addedCount = 0;
      let updatedCount = 0;
      let deletedCount = 0;

      // Separate contacts into batches for efficient processing
      const contactsToUpsert: any[] = [];
      const contactsToDelete: string[] = [];

      // Process changes in batches - filter for contacts with email addresses (Outlook only)
      for (const contact of contacts) {
        if (contact['@removed']) {
          contactsToDelete.push(contact.id);
          deletedCount++;
        } else {
          // Apply filtering only for Outlook context
          if (this.isOutlookContext) {
            const email = contact.emailAddresses?.[0]?.address;
            if (email && email.trim() !== '') {
              contactsToUpsert.push(contact);
            }
          } else {
            // For Teams, include all contacts
            contactsToUpsert.push(contact);
          }
        }
      }

      // Batch process deletions
      if (contactsToDelete.length > 0) {
        await this.store.bulkMarkContactsDeleted(contactsToDelete);
      }

      // Batch process upserts
      if (contactsToUpsert.length > 0) {
        const normalizedContacts = await this.normalizer.normalizeContacts(contactsToUpsert, userUpn, 'personal');
        
        // Check which contacts are new vs updates
        const existingContacts = await Promise.all(
          normalizedContacts.map(contact => this.store.getContact(contact.id))
        );
        
        existingContacts.forEach((existing, index) => {
          if (existing) {
            updatedCount++;
          } else {
            addedCount++;
          }
        });

        await this.store.bulkUpsertContacts(normalizedContacts);
      }

      // Update meta with new delta token and sync time
      await this.updateIncrementalSyncMeta(newDeltaLink, addedCount, deletedCount);
      
      // Rebuild search index if contacts were added, updated, or deleted
      if (addedCount > 0 || updatedCount > 0 || deletedCount > 0) {
        await this.buildSearchIndex(options);
      }
      
      // Log the delta token for debugging
      if (newDeltaLink) {
        console.log('Personal incremental sync completed with new delta token:', newDeltaLink.substring(0, 100) + '...');
      } else {
        console.warn('Personal incremental sync completed but no new delta token was received, keeping existing one');
      }

      onProgress?.({
        current: contacts.length,
        total: contacts.length,
        phase: 'incremental',
        message: `Personal incremental sync complete! +${addedCount} -${deletedCount} ~${updatedCount}`
      });

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Personal incremental sync failed');
      console.error('Personal incremental sync failed:', err);
      onError?.(err);
      throw err;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Generate delta token for future incremental syncs
   */
  async generateDeltaToken(accessToken: string, userUpn: string): Promise<void> {
    console.log('Generating personal delta token...');
    try {
      const meta = await this.store.getMeta();
      let deltaResponse;
      if (meta.personalDeltaToken) {
        // Use existing delta token to get a fresh one (no contact download)
        console.log('Refreshing existing personal delta token...');
        deltaResponse = await this.processDeltaQuery(accessToken, meta.personalDeltaToken, userUpn);
      } else {
        // Generate initial delta token (no contact download)
        console.log('Generating initial personal delta token...');
        deltaResponse = await this.processDeltaQuery(accessToken, '', userUpn);
      }
      if (deltaResponse.deltaToken) {
        // Re-read metadata to get latest state including Corporate/Internal delta tokens
        const latestMeta = await this.store.getMeta();
        
        // Update meta with the new delta token and ensure bootstrap is complete
        await this.store.setMeta({
          ...latestMeta,  // Use latest metadata instead of stale meta
          personalDeltaToken: deltaResponse.deltaToken,
          personalBootstrapComplete: true // Ensure bootstrap is marked complete
        });
        console.log('Personal delta token generated successfully');
      }
    } catch (error) {
      console.warn('Failed to generate personal delta token:', error);
    }
  }
}
